import { useEffect, useState } from "react";
import { BellRing, HandCoins, HeartHandshake, UserPlus, MessageCircle, Coins } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";

const CATS = [
  { key: "tips", label: "Tips received", desc: "When someone sends you a tip", icon: HandCoins },
  { key: "thank_you", label: "Thank-yous", desc: "Heartfelt thank-yous from givers", icon: HeartHandshake },
  { key: "follow", label: "New followers", desc: "When someone starts supporting you", icon: UserPlus },
  { key: "comments", label: "Comments & reactions", desc: "Comments and likes on your posts & photos", icon: MessageCircle },
  { key: "jar_recap", label: "Daily jar recap", desc: "Your evening summary of today's jar share", icon: Coins },
];

export default function NotifPrefsDialog({ open, onOpenChange }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/notifications/prefs").then((r) => setPrefs(r.data)).catch(() => {});
  }, [open]);

  const toggle = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaving(true);
    try {
      const { data } = await api.put("/notifications/prefs", { [key]: value });
      setPrefs(data);
    } catch (e) {
      toast.error("Couldn't save that — please try again.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notif-prefs-dialog" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="font-serif text-2xl text-bronze flex items-center justify-center gap-2">
            <BellRing className="w-5 h-5 text-copper" /> Notification settings
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Choose which push alerts you'd like. You'll still see everything in your notification bell.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 mt-1">
          {CATS.map((c) => (
            <div key={c.key} data-testid={`pref-row-${c.key}`}
              className="flex items-center gap-3 rounded-2xl border border-border/70 bg-white p-3.5">
              <span className="w-9 h-9 rounded-full bg-copper/12 text-copper flex items-center justify-center shrink-0">
                <c.icon className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-medium text-bronze leading-tight">{c.label}</p>
                <p className="text-xs text-muted-foreground leading-snug">{c.desc}</p>
              </div>
              <Switch
                data-testid={`pref-switch-${c.key}`}
                checked={prefs ? !!prefs[c.key] : true}
                disabled={!prefs || saving}
                onCheckedChange={(v) => toggle(c.key, v)}
              />
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-2">
          Important moments like being verified or reaching your goal are always on 💛
        </p>
      </DialogContent>
    </Dialog>
  );
}
